/* eslint-disable react/require-default-props */
import React from "react";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Typography,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { useIntl } from "../../../../../_metronic/i18n/customUseIntl";
import type { Attachment } from "../../../hooks/notifications/useNotificationUpload";
import { getAttachmentDisplayName } from "../../../utils/string-utils";
import { notificationAttachmentsStyles } from "../../../../ThemeProvider";

type NotificationAttachmentsProps = {
  attachments: Attachment[];
  isSaving: boolean;
  uploadErrors?: string[];
  invalidFileErrors?: string[];
  onFileChange: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  onChipDelete: (key: string) => void;
  onToggleRemoveAttachment: (key: string) => void;
};

const NotificationAttachments = ({
  attachments,
  isSaving,
  uploadErrors,
  invalidFileErrors,
  onFileChange,
  onChipDelete,
  onToggleRemoveAttachment,
}: NotificationAttachmentsProps) => {
  const intl = useIntl();
  const classes = notificationAttachmentsStyles();

  const newAttachments = attachments.filter((a) => a.source === "new");
  const existingAttachments = attachments.filter(
    (a) => a.source === "existing",
  );

  return (
    <>
      <Box className={classes.uploadContainer}>
        <Button
          variant="contained"
          color="default"
          component="label"
          startIcon={<CloudUploadIcon />}
          disabled={isSaving}
        >
          {intl.formatMessage({ id: "NOTIFICATIONS.ADD_FILES" })}
          <input
            name="attachments"
            type="file"
            accept="image/*,application/pdf"
            hidden
            multiple
            onChange={onFileChange}
          />
        </Button>

        <ul className={classes.fileList}>
          {newAttachments.map((attachment) => (
            <li key={attachment.key} className={classes.fileItem}>
              <Chip
                label={attachment.name}
                onDelete={() => onChipDelete(attachment.key)}
                disabled={isSaving}
              />
              {isSaving && attachment.uploadProgress !== undefined && (
                <LinearProgress
                  variant="determinate"
                  value={attachment.uploadProgress}
                  className={classes.progressBar}
                />
              )}
            </li>
          ))}
        </ul>
      </Box>

      {uploadErrors && uploadErrors.length > 0 && (
        <Box className={classes.uploadErrorsContainer}>
          {uploadErrors.map((err) => (
            <Typography key={err} variant="body2" color="error">
              {err}
            </Typography>
          ))}
        </Box>
      )}

      {invalidFileErrors && invalidFileErrors.length > 0 && (
        <Box className={classes.uploadErrorsContainer}>
          {invalidFileErrors.map((name) => (
            <Typography key={name} variant="body2" color="error">
              {intl.formatMessage(
                { id: "NOTIFICATIONS.INVALID_FILE_TYPE" },
                { name },
              )}
            </Typography>
          ))}
        </Box>
      )}

      {existingAttachments.length > 0 && (
        <Box>
          <ul className={classes.fileList}>
            {existingAttachments.map((attachment) => (
              <li
                key={attachment.key}
                className={classes.fileItem}
                style={{ opacity: attachment.isRemoved ? 0.5 : 1 }}
              >
                <Chip
                  label={getAttachmentDisplayName(attachment.key)}
                  onDelete={() => onToggleRemoveAttachment(attachment.key)}
                  disabled={isSaving}
                  color={attachment.isRemoved ? "default" : "primary"}
                  variant={attachment.isRemoved ? "outlined" : "default"}
                />
              </li>
            ))}
          </ul>
        </Box>
      )}
    </>
  );
};

export default NotificationAttachments;
